import {useCallback, useEffect, useRef, useState} from 'react';

import {ProcessesPluginBase} from 'processes/graph-builder-processes/platform/ProcessesPluginBase';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import validateProcessFlowchart from 'processes/graph-builder-processes/validation/validateProcessFlowchart';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import buildValidationFlowchartFromStaticData from './buildValidationFlowchartFromStaticData';
import {StaticTemplateFlowchart} from './processFlowchartConfigEditorConstants';
import {FlowchartConfigWithMetadata} from './redux/flowchartConfigAutosaveManagerCallbacks';
import {
  FlowchartConfigAutosaverPausedCondition,
  FlowchartConfigAutosaverPausedConditionType,
} from './redux/reduceFlowchartConfig';
import {ConfigurableTaskDefinitionGQL} from './useAllSchemasById';
import {useApplicationFlowchartValidationContext} from './useFlowchartValidationContext';

// Exporting for testing
export interface UseFlowchartValidationProps {
  draftFlowchartConfig: StaticTemplateFlowchart;
  flowchartConfigId: string;
  flowchartConfigMetadata: FlowchartConfigWithMetadata['metadata'];
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  taskDefinitions: Array<ConfigurableTaskDefinitionGQL>;
  // Note, this must be a memoized callback/plain function to avoid unnecessary re-renders
  setIsFlowchartConfigAutosaverPaused: (
    condition: FlowchartConfigAutosaverPausedConditionType,
    conditionValue: boolean
  ) => void;
  didInitialFlowchartHaveChainedRouters: boolean;
  plugin: ProcessesPluginBase;
  applicationValidationContext?: Record<string, unknown>;
}

// Exporting for testing
export interface UseFlowchartValidationReturn {
  validationErrors: Array<ProcessFlowchartValidationError>;
  validationErrorsRef: React.MutableRefObject<Array<ProcessFlowchartValidationError>>;
  setValidationErrors: (errors: Array<ProcessFlowchartValidationError>) => void;
  validateFlowchart: (draftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata) => void;
}

export function validateFlowchartData(
  staticFlowchartData: StaticTemplateFlowchart,
  flowchartConfigId: string,
  metadata: FlowchartConfigWithMetadata['metadata'],
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL,
  didInitialFlowchartHaveChainedRouters: boolean,
  plugin: ProcessesPluginBase,
  applicationValidationContext?: Record<string, unknown>
): Array<ProcessFlowchartValidationError> {
  const {flowchart, designSpace} = buildValidationFlowchartFromStaticData({
    id: flowchartConfigId,
    staticNodesData: staticFlowchartData.nodesStaticData,
    staticEdgesData: staticFlowchartData.edgesStaticData,
    options: {
      transitions: metadata.transitions,
      schemaMapperConfigs: metadata.schemaMapperConfigs,
      rootTaskSchema,
      customMappingDataByEdgeId: metadata.customMappingDataByEdgeId,
      transitionSourceByNodeIdPair: metadata.transitionSourceByNodeIdPair,
      currentVersionId: metadata.currentVersionId,
      didInitialFlowchartHaveChainedRouters: metadata.didInitialFlowchartHaveChainedRouters,
      flowchartTypeName: metadata.flowchartTypeName,
      options: metadata.options,
    },
  });
  const validationResult = validateProcessFlowchart({
    flowchart,
    designSpace,
    usingFlowchartFirstConfig: true,
    didInitialFlowchartHaveChainedRouters,
    plugin,
    applicationValidationContext,
  });
  return validationResult.errors;
}

// Module exporting to support test stubbing
export const ValidationModule = {
  validateFlowchartData,
};

export function useFlowchartValidationAndAutosaverControl({
  draftFlowchartConfig,
  flowchartConfigId,
  flowchartConfigMetadata,
  rootTaskSchema,
  setIsFlowchartConfigAutosaverPaused,
  didInitialFlowchartHaveChainedRouters,
  plugin,
}: UseFlowchartValidationProps): UseFlowchartValidationReturn {
  const {context: applicationValidationContext, isLoaded: isApplicationValidationContextLoaded} =
    useApplicationFlowchartValidationContext(rootTaskSchema.executionType, draftFlowchartConfig);
  const isApplicationValidationContextLoadedRef = useRef(isApplicationValidationContextLoaded);

  const [validationErrors, _setValidationErrors] = useState<Array<ProcessFlowchartValidationError>>(() =>
    ValidationModule.validateFlowchartData(
      draftFlowchartConfig,
      flowchartConfigId,
      flowchartConfigMetadata,
      rootTaskSchema,
      didInitialFlowchartHaveChainedRouters,
      plugin,
      applicationValidationContext
    )
  );

  const validationErrorsRef = useRef(validationErrors);

  const setValidationErrors = useCallback((newValidationErrors) => {
    _setValidationErrors(newValidationErrors);
    validationErrorsRef.current = newValidationErrors;
  }, []);

  const validateFlowchart = useCallback(
    (draftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata) => {
      const currentValidationErrors = ValidationModule.validateFlowchartData(
        draftFlowchartConfigAndMetadata.staticFlowchart,
        flowchartConfigId,
        draftFlowchartConfigAndMetadata.metadata,
        rootTaskSchema,
        didInitialFlowchartHaveChainedRouters,
        plugin,
        // Use the validation context computed at the top level. The hook values will update automatically
        // when the draft flowchart changes. Application teams can provide a custom validation context if
        // needed.
        applicationValidationContext
      );
      setValidationErrors(currentValidationErrors);
    },
    [
      flowchartConfigId,
      rootTaskSchema,
      didInitialFlowchartHaveChainedRouters,
      plugin,
      setValidationErrors,
      applicationValidationContext,
    ]
  );

  useEffect(() => {
    if (isApplicationValidationContextLoaded && !isApplicationValidationContextLoadedRef.current) {
      isApplicationValidationContextLoadedRef.current = true;
      validateFlowchart({
        staticFlowchart: draftFlowchartConfig,
        metadata: flowchartConfigMetadata,
      });
    } else if (!isApplicationValidationContextLoaded) {
      isApplicationValidationContextLoadedRef.current = false;
    }
  }, [
    applicationValidationContext,
    isApplicationValidationContextLoaded,
    validateFlowchart,
    draftFlowchartConfig,
    flowchartConfigMetadata,
  ]);

  const setPausedForValidationErrors = useCallback(
    (isPaused: boolean) => {
      setIsFlowchartConfigAutosaverPaused(
        FlowchartConfigAutosaverPausedCondition.HAS_VALIDATION_ERRORS,
        isPaused
      );
    },
    [setIsFlowchartConfigAutosaverPaused]
  );
  useAutosaverPausingForValidationErrors(validationErrors, setPausedForValidationErrors);

  return {
    validationErrors,
    validationErrorsRef,
    setValidationErrors,
    validateFlowchart,
  };
}

const useAutosaverPausingForValidationErrors = (
  validationErrors: Array<ProcessFlowchartValidationError>,
  setAutosaverPaused: (isPaused: boolean) => void
): void => {
  useEffect(() => {
    const blockingErrors = validationErrors.filter((error) => error.blocksPersistence);
    setAutosaverPaused(blockingErrors.length > 0);
  }, [validationErrors, setAutosaverPaused]);
};
